import axios from 'axios'
import { API_BASE_URL } from '../constants/system'
import { SkillDraft, SkillDuplicateLevel, SkillInfo, SkillLibraryResponse, SkillTestRecord } from '../types/agent'

const BASE = `${API_BASE_URL}/agent/skills`

/** A skill's route; platform ids ("core:<name>") are encoded. */
const skillUrl = (id: string, rest = '') => `${BASE}/${encodeURIComponent(id)}${rest}`
const q = (customer: string) => `?customer=${encodeURIComponent(customer)}`

export type SkillScope = 'private' | 'organizational' | 'public' | 'visible'

/** One file in a skill bundle; content is base64-encoded. */
export interface SkillBundleFile {
  path: string
  content: string
}

export interface SkillBundle {
  name: string
  files: SkillBundleFile[]
}

export const agentSkillsApi = {
  async list(customer: string, scope: SkillScope = 'visible', opts?: { includeCore?: boolean; sessionId?: string }): Promise<SkillInfo[]> {
    const core = opts?.includeCore ? '&include_core=1' : ''
    const session = opts?.sessionId ? `&session_id=${encodeURIComponent(opts.sessionId)}` : ''
    const res = await axios.get<{ skills?: SkillInfo[] }>(`${BASE}?customer=${customer}&scope=${scope}${core}${session}`)
    return res.data.skills || []
  },

  async get(id: string, customer: string): Promise<SkillInfo> {
    const res = await axios.get<SkillInfo>(`${BASE}/${id}?customer=${customer}`)
    return res.data
  },

  async bundle(id: string, customer: string): Promise<SkillBundle> {
    const res = await axios.get<SkillBundle>(`${BASE}/${id}/bundle?customer=${customer}`)
    return res.data
  },

  async remove(id: string, customer: string): Promise<void> {
    await axios.delete(`${BASE}/${id}?customer=${customer}`)
  },

  /** Manager: move a live private or Company-level skill to Bayes Price skills (the same row). */
  async promotePublic(id: string, customer: string): Promise<void> {
    await axios.post(`${BASE}/${id}/promote-public?customer=${customer}`)
  },

  /** The Skills tab's tiers. With sessionId each row says whether that session has it installed. */
  async library(customer: string, opts?: { sessionId?: string; job?: string }): Promise<SkillLibraryResponse> {
    const session = opts?.sessionId ? `&session_id=${encodeURIComponent(opts.sessionId)}` : ''
    const job = opts?.job ? `&job=${encodeURIComponent(opts.job)}` : ''
    const res = await axios.get<SkillLibraryResponse>(`${BASE}/library${q(customer)}${session}${job}`)
    return res.data
  },

  /** "Write one from scratch": a new private draft. */
  async create(customer: string, draft: SkillDraft): Promise<SkillInfo> {
    return (await axios.post<SkillInfo>(`${BASE}${q(customer)}`, draft)).data
  },

  /** A new SKILL.md (and files) for a draft or a live skill. */
  async update(id: string, customer: string, draft: SkillDraft): Promise<SkillInfo> {
    return (await axios.put<SkillInfo>(`${skillUrl(id)}${q(customer)}`, draft)).data
  },

  /** Save a draft to My skills. */
  async publish(id: string, customer: string): Promise<SkillInfo> {
    return (await axios.post<SkillInfo>(`${skillUrl(id, '/publish')}${q(customer)}`)).data
  },

  async recordTest(id: string, customer: string, sessionId: string): Promise<{ tests: SkillTestRecord[] }> {
    return (await axios.post<{ tests: SkillTestRecord[] }>(`${skillUrl(id, '/tests')}${q(customer)}`, { session_id: sessionId })).data
  },

  async promote(id: string, customer: string): Promise<SkillInfo> {
    return (await axios.post<SkillInfo>(`${skillUrl(id, '/promote')}${q(customer)}`)).data
  },

  async demote(id: string, customer: string): Promise<SkillInfo> {
    return (await axios.delete<SkillInfo>(`${skillUrl(id, '/promote')}${q(customer)}`)).data
  },

  /** The caller's own copy of any skill they can read. */
  async copy(id: string, customer: string): Promise<SkillInfo> {
    return (await axios.post<SkillInfo>(`${skillUrl(id, '/copy')}${q(customer)}`)).data
  },

  /** A copy of a Bayes Price skill, keeping its name, in My skills or (Manager) Company-level. 409 when the name is taken there. */
  async duplicate(id: string, customer: string, level: SkillDuplicateLevel): Promise<SkillInfo> {
    return (await axios.post<SkillInfo>(`${skillUrl(id, '/duplicate')}${q(customer)}`, { level })).data
  },

  /** Manager: turn a Bayes Price skill off for one customer, or back on. */
  async setOffForCustomer(id: string, customer: string, off: boolean): Promise<void> {
    const url = `${skillUrl(id, '/customer-off')}${q(customer)}`
    if (off) await axios.put(url)
    else await axios.delete(url)
  },

  /** Manager: save a draft over the Bayes Price skill of the same name. */
  async publishToBayesPrice(id: string, customer: string): Promise<SkillInfo> {
    return (await axios.post<SkillInfo>(`${skillUrl(id, '/publish')}${q(customer)}&target=bayesprice`)).data
  },

  /**
   * A new chat to test a skill in, on the given jobs, titled "Skill test: <name>"
   * so it reads as one in History. Returns the chat's id.
   */
  async createTestChat(customer: string, skillName: string, job: string, jobs: string[]): Promise<string> {
    const res = await axios.post<{ id: string }>(`${API_BASE_URL}/agentkit/agent/${encodeURIComponent(customer)}/session`, { job, jobs })
    const id = res.data.id
    try {
      await axios.patch(`${API_BASE_URL}/agent/session/${encodeURIComponent(id)}`, { title: `Skill test: ${skillName}` })
    } catch (error) {
      await agentSkillsApi.deleteTestChat(id)
      throw error
    }
    return id
  },

  /** Removes a test chat whose test did not start, so no empty chat is left in History. Best effort. */
  async deleteTestChat(sessionId: string): Promise<void> {
    await axios.delete(`${API_BASE_URL}/agent/session/${encodeURIComponent(sessionId)}`).catch(() => undefined)
  },
}
